// @ts-nocheck
import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bool } from 'prop-types';

import { Page, LayoutSingleColumn } from '../components';
import TopbarContainer from '../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../containers/FooterContainer/FooterContainer';
import GuidedAppFlow from '../components/Apps/GuidedAppFlow';
import SimpleAppExperience from '../components/Apps/SimpleAppExperience';
import { getBxApp } from '../config/bxApps';
import { getSimpleAppExperience, isSimpleConsumerApp } from '../config/simpleAppExperiences';
import { resolveBxAppLaunchHref, isExternalHref } from '../config/railNavigation';
import { useActivityCart } from '../context/ActivityCartContext';
import { PRODUCT_BRAND } from '../config/productBrand';
import { isScrollingDisabled } from '../ducks/ui.duck';

import css from './BxAppHub.module.css';
import simpleCss from '../containers/SimpleLandingPage/SimpleLandingPage.module.css';

const QuietAppShell = ({ children, appName }) => (
  <div className={simpleCss.root} data-testid="quiet-app-shell">
    <div className={simpleCss.shell}>
      <header className={simpleCss.topBar}>
        <Link to="/landing" className={simpleCss.backBtn}>
          ← Apps
        </Link>
        <span className={simpleCss.brandMark}>{appName || PRODUCT_BRAND.name}</span>
      </header>
      <main id="main-content" className={simpleCss.main}>
        {children}
      </main>
    </div>
  </div>
);

const BxAppHubComponent = ({ scrollingDisabled }) => {
  const { appId } = useParams<{ appId: string }>();
  const location = useLocation();
  const { getDraft } = useActivityCart();

  const app = getBxApp(appId || '');
  const useSimple = app && isSimpleConsumerApp(app.id);
  const experience = app ? getSimpleAppExperience(app.id) : null;
  const shellName = experience?.displayName || app?.shortName;
  const launchHref = app ? resolveBxAppLaunchHref(app) : '/';
  const isExternalLaunch = /^https?:\/\//.test(launchHref);
  const draftParam = new URLSearchParams(location.search).get('draft');
  const draft = draftParam ? getDraft(draftParam) : null;

  if (!app) {
    return (
      <Page title="App not found" scrollingDisabled={scrollingDisabled}>
        <QuietAppShell>
          <div className={css.container}>
            <h1>App not found</h1>
            <Link to="/landing">Return to apps</Link>
          </div>
        </QuietAppShell>
      </Page>
    );
  }

  if (useSimple) {
    return (
      <Page title={shellName || app.name} description={app.tagline} scrollingDisabled={scrollingDisabled}>
        <QuietAppShell appName={shellName}>
          <div className={css.simpleContainer}>
            <SimpleAppExperience app={app} />
          </div>
        </QuietAppShell>
      </Page>
    );
  }

  return (
    <Page title={app.name} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        className={css.pageRoot}
      >
        <div className={css.container} data-testid={`bx-app-hub-${app.id}`}>
          <header className={css.hero} style={{ '--app-accent': app.accent }}>
            <p className={css.eyebrow}>BX App</p>
            <h1 className={css.title}>{app.name}</h1>
            <p className={css.tagline}>{app.tagline}</p>
            <p className={css.description}>{app.description}</p>
            <div className={css.heroLinks}>
              <Link to="/activity" className={css.secondaryLink}>
                Activity order book
              </Link>
              {isExternalLaunch ? (
                <a href={launchHref} className={css.quickLaunch} rel="noopener noreferrer">
                  Open settlement rail →
                </a>
              ) : (
                <Link to={launchHref} className={css.quickLaunch}>
                  Skip to live app →
                </Link>
              )}
            </div>
          </header>

          {draft ? (
            <p className={css.resumeNote}>
              Resuming saved flow — step {draft.stepIndex + 1} of {app.steps.length}
            </p>
          ) : null}

          <GuidedAppFlow
            app={app}
            draftId={draft?.id}
            initialStep={draft?.stepIndex ?? 0}
            initialFormData={draft?.formData ?? {}}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

BxAppHubComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const BxAppHub = compose(connect(mapStateToProps))(BxAppHubComponent);

export default BxAppHub;
